import {useQuery} from '@tanstack/react-query'
// import { useAuthUser } from 'react-auth-kit';

import {PackageListSchemaResponse, PackageSchemaResponse} from '../domain'

import {useAuthUser} from 'react-auth-kit'
import {useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {API} from 'src/feature/bite/domain'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import useRequestParams from 'src/feature/bite/hook/request'
import {useGetDetailTermsAndConditions} from 'src/feature/store/context/useQuery'
import {usePackage} from '../hook'

export function useGetActiveList() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {listActiveFilter, listActivePackagesSet, listFilter} = usePackage()

  const userID = auth()?.id

  return useQuery(
    ['PackageActiveList', listActiveFilter],
    async () => {
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Subscription/GetSubscriptionPackageList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listActiveFilter,
          // storeID: '996225251718',
          userID,
          merchantID: auth()?.merchantID,
          language: headers.Language,
          timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = PackageListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = PackageListSchemaResponse.parse(Data)

      const filteredByPaymentLinkEnable = parse.response.dataList.filter(
        (item) => item?.paymentLinkEnable && item?.paymentLinkEnable === true
      )

      listActivePackagesSet({
        ...parse.response,
        dataList: filteredByPaymentLinkEnable || [],
      })

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetList() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {listFilter, listSet} = usePackage()

  const userID = auth()?.id

  return useQuery(
    ['PackageList', listFilter],
    async () => {
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Subscription/GetSubscriptionPackageList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          // storeID: '996225251718',
          userID,
          merchantID: auth()?.merchantID,
          language: headers.Language,
          timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = PackageListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = PackageListSchemaResponse.parse(Data)

      listSet(parse.response)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetDetail() {
  const router = useNavigate()
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {detailSet} = usePackage()
  let {packageId} = useParams()
  const {data: isTermsAndConditionsAvailable, isLoading: isLoadingTermsAvailable} =
    useGetDetailTermsAndConditions()

  const userID = auth()?.id
  const authData = useAuthUser()

  return useQuery(
    ['PackageDetail', packageId],
    async () => {
      if (!packageId) return true

      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Subscription/GetSubscriptionPackage`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          id: packageId,
          userID,
          language: headers.Language,
          timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = PackageSchemaResponse.safeParse(Data)
      const parse = PackageSchemaResponse.parse(Data)

      if (!parse.response) {
        router('/error/packages')
      }

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      let packageRes = parse.response

      // Start: Get HTML Items
      if (packageRes?.infoContentData) {
        let faqs = [
          {
            title: 'Feature and Benefits',
          },
          {
            title: 'Eligibility',
          },
          {
            title: 'Additional Benefits',
          },
          {
            title: 'Cancellation and Refund Policy',
          },
        ]

        await Promise.all(
          packageRes?.infoContentData?.map(async (item) => {
            const dataUrl = item?.contentURL
            if (dataUrl) {
              const res = await fetch(dataUrl, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${auth()?.token}`,
                },
              })
              const dataValue = await res.text()
              const parser = new DOMParser()
              const doc = parser.parseFromString(dataValue, 'text/html')
              const contentView = doc.querySelector('#contentView')

              if (contentView) {
                item.data = contentView.innerHTML || ''
              }
            }

            return item
          })
        )

        if (packageRes?.termsAndConditions && isTermsAndConditionsAvailable) {
          const termsUrl = packageRes?.termsAndConditions
          if (termsUrl) {
            const res = await fetch(termsUrl, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${auth()?.token}`,
              },
            })

            const dataValue = await res.text()
            const parser = new DOMParser()
            const doc = parser.parseFromString(dataValue, 'text/html')
            const contentView = doc.querySelector('#contentView')

            if (contentView) {
              packageRes.termsAndConditions = contentView.innerHTML || ''
            } else packageRes.termsAndConditions = ''
          }

          packageRes?.infoContentData?.splice(3, 0, {
            name: 'Terms and Conditions',
            data: packageRes?.termsAndConditions || '',
          })
        }

        await Promise.all(
          faqs.map((faq) => {
            const item = packageRes?.infoContentData?.find((item) => item.name === faq.title)
            if (!item) {
              packageRes?.infoContentData?.push({
                name: faq.title,
                data: '',
              })
            }
          })
        )
      }

      // End: Get HTML Items

      // Start: Get Description Meta

      if (packageRes?.description) {
        const dataUrl = packageRes?.description

        if (dataUrl) {
          const res = await fetch(dataUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth()?.token}`,
            },
          })

          const dataValue = await res.text()
          const parser = new DOMParser()
          const doc = parser.parseFromString(dataValue, 'text/html')
          const contentView = doc.querySelector('#contentView')

          if (contentView) {
            packageRes.description = contentView.innerHTML || ''
          } else packageRes.description = ''
        }
      }

      // End: Get HTML Items

      const ResponsePackageItems = await fetch(
        `${API}/Subscription/GetSubscriptionPackageItemList`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            filterList: [{operator: 1, key: 'sbscrptnPckgID', value: [packageId]}],
            orderList: [],
            paging: {currentPageNumber: 1, pageSize: 100},
            userID,
            language: headers.Language,
          }),
        }
      )

      const DataPackageItems = await ResponsePackageItems.json()

      const _packageRes = {
        ...packageRes,
        items: DataPackageItems?.response?.dataList || [],
      }

      detailSet(_packageRes)

      return true
    },
    {
      enabled: !!packageId && !isLoadingTermsAvailable,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetDetailForNew() {
  const {auth, headers} = useRequestParams()
  const {categoriesSet, familySet, visibilitySet} = usePackage()

  const userID = auth()?.id

  return useQuery(
    ['ProductNew'],
    async () => {
      const CategoryResponse = await fetch(`${API}/Category/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          language: headers.Language,
        }),
      })

      const CategoryData = await CategoryResponse.json()

      const FamilyResponse = await fetch(`${API}/StoreProductFamily/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          language: headers.Language,
        }),
      })

      const FamilyData = await FamilyResponse.json()

      const VisibilityResponse = await fetch(`${API}/Visibility/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          language: headers.Language,
        }),
      })

      const VisibilityData = await VisibilityResponse.json()

      categoriesSet(CategoryData?.response?.categories || [])
      familySet(FamilyData?.response?.families || [])
      visibilitySet(VisibilityData?.response?.visibilities || [])

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onError: (error) => {},
    }
  )
}
